/**
 * Event-scoped ticket request page handler.
 * Sends requests to the secure request_event_ticket_link endpoint.
 */

const __ = window.__ || function(s) { return s; };

class TicketRequestPage {
  constructor() {
    this.root = document.getElementById('ticket-request-root');
    if (!this.root) return;

    this.eventId = this.root.dataset.eventId;
    this.eventSlug = this.root.dataset.eventSlug;
    this.form = document.getElementById('request-form');
    this.emailInput = document.getElementById('email-input');
    this.submitButton = this.form?.querySelector('button[type="submit"]');
    this.buttonLabel = this.submitButton?.querySelector('[data-button-label]');
    this.buttonSpinner = this.submitButton?.querySelector('[data-button-spinner]');
    this.successState = document.getElementById('success-state');
    this.privacyNote = this.root.querySelector('.request-privacy-note');
    this.backLink = this.successState?.querySelector('.request-success-back');
    this.inputError = this.root.querySelector('.request-input-error');
    this.captchaContainer = document.getElementById('captcha-container');

    this.texts = {
      loading: this.root.dataset.textLoading || 'Sending request…',
      success: this.root.dataset.textSuccess || 'If a registration exists, a link was sent.',
      error: this.root.dataset.textError || 'We could not process your request.',
      errorRate: this.root.dataset.textErrorRate || 'Too many attempts. Please wait.',
      errorRateWithTime: this.root.dataset.textErrorRateWithTime || 'Too many attempts. Please wait {seconds} seconds.',
      errorCaptcha: this.root.dataset.textErrorCaptcha || 'Please complete the security check.',
      formMissing: this.root.dataset.textFormMissing || 'Please enter your email address.',
      emailInvalid: this.root.dataset.textEmailInvalid || 'Please enter a valid email address.',
    };

    this.captchaToken = null;
    this.isSubmitting = false;
    this.locale = navigator.language?.split('-')[0] || 'de';

    this.init();
  }

  init() {
    if (!this.form) return;

    this.form.addEventListener('submit', (e) => this.handleSubmit(e));
    this.initCaptcha();
    this.initValidation();

    // Auto-focus email input after entrance animation
    setTimeout(() => {
      if (this.emailInput && !this.emailInput.value) {
        this.emailInput.focus();
      }
    }, 500);
  }

  initCaptcha() {
    if (!this.captchaContainer) return;

    // Try Turnstile first, then hCaptcha
    if (window.turnstile && window.TURNSTILE_SITE_KEY) {
      window.turnstile.render(this.captchaContainer, {
        sitekey: window.TURNSTILE_SITE_KEY,
        callback: (token) => { this.captchaToken = token; },
        'expired-callback': () => { this.captchaToken = null; },
      });
    } else if (window.hcaptcha && window.HCAPTCHA_SITE_KEY) {
      window.hcaptcha.render(this.captchaContainer, {
        sitekey: window.HCAPTCHA_SITE_KEY,
        callback: (token) => { this.captchaToken = token; },
        'expired-callback': () => { this.captchaToken = null; },
      });
    }
    // If no captcha provider, the backend will skip verification
  }

  initValidation() {
    if (!this.emailInput) return;

    let debounceTimer;
    this.emailInput.addEventListener('input', () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        const val = this.emailInput.value.trim();
        if (!val) {
          this.emailInput.classList.remove('is-valid', 'is-invalid');
          this.emailInput.removeAttribute('aria-invalid');
          if (this.inputError) this.inputError.hidden = true;
          return;
        }
        if (this.isValidEmail(val)) {
          this.emailInput.classList.remove('is-invalid');
          this.emailInput.classList.add('is-valid');
          this.emailInput.setAttribute('aria-invalid', 'false');
          if (this.inputError) this.inputError.hidden = true;
        } else {
          this.emailInput.classList.remove('is-valid');
          this.emailInput.classList.add('is-invalid');
          this.emailInput.setAttribute('aria-invalid', 'true');
          if (this.inputError) this.inputError.hidden = false;
        }
      }, 300);
    });
  }

  async handleSubmit(e) {
    e.preventDefault();

    if (this.isSubmitting) return;

    const email = this.emailInput?.value?.trim();
    if (!email) {
      this.showToast(this.texts.formMissing, 'error');
      this.emailInput?.focus();
      return;
    }

    // Validate email format
    if (!this.isValidEmail(email)) {
      this.showToast(this.texts.emailInvalid, 'error');
      this.emailInput?.focus();
      return;
    }

    this.setLoading(true);

    try {
      const response = await this.callApi('request_event_ticket_link', {
        event_id: this.eventId,
        email: email,
        locale: this.locale,
      });

      // Always show success (neutral response for privacy)
      this.showSuccess();

    } catch (error) {
      if (error.status === 429) {
        // Show retry time if available
        const retryAfter = error.retryAfter;
        if (retryAfter && this.texts.errorRateWithTime) {
          this.showToast(this.texts.errorRateWithTime.replace('{seconds}', retryAfter), 'error');
        } else {
          this.showToast(this.texts.errorRate, 'error');
        }
      } else if (error.status === 403) {
        this.showToast(this.texts.errorCaptcha, 'error');
      } else {
        this.showToast(this.texts.error, 'error');
      }
    } finally {
      this.setLoading(false);
      this.resetCaptcha();
    }
  }

  async callApi(method, args) {
    const headers = {};

    // Send captcha token as header (backend expects X-Captcha-Token)
    if (this.captchaToken) {
      headers['X-Captcha-Token'] = this.captchaToken;
    }

    const { res: response, normalized } = await window.citaFetchJson(
      `/api/method/cita_events.api.ticket.guest_view.${method}`,
      { method: 'POST', headers, body: args }
    );
    if (!normalized.success) {
      const error = new Error(normalized.toast || 'Request failed');
      error.status = normalized.httpStatus || response.status || 400;
      const retryAfter = response.headers.get('Retry-After');
      if (retryAfter) {
        error.retryAfter = parseInt(retryAfter, 10);
      }
      throw error;
    }
    return normalized;
  }

  setLoading(loading) {
    this.isSubmitting = loading;

    if (this.submitButton) {
      this.submitButton.disabled = loading;
    }
    if (this.buttonLabel) {
      this.buttonLabel.hidden = loading;
    }
    if (this.buttonSpinner) {
      this.buttonSpinner.hidden = !loading;
    }
  }

  showSuccess() {
    if (this.form) {
      this.form.style.transition = 'opacity 0.25s ease, transform 0.25s ease';
      this.form.style.opacity = '0';
      this.form.style.transform = 'translateY(-8px)';
      setTimeout(() => {
        this.form.hidden = true;
        this.form.style.removeProperty('transition');
        this.form.style.removeProperty('opacity');
        this.form.style.removeProperty('transform');
        if (this.successState) {
          this.successState.hidden = false;
        }
        if (this.privacyNote) {
          this.privacyNote.hidden = true;
        }
        // Focus back-link for keyboard users
        if (this.backLink) {
          this.backLink.focus();
        }
      }, 250);
    } else if (this.successState) {
      this.successState.hidden = false;
    }
  }

  showToast(message, type = 'info') {
    if (typeof window.showToast === 'function') {
      window.showToast(message, type);
      return;
    }
    // site.min.js loads deferred — retry briefly
    const tryShowToast = (attempts = 0) => {
      if (typeof window.showToast === 'function') {
        window.showToast(message, type);
        return;
      }
      if (attempts < 10) {
        setTimeout(() => tryShowToast(attempts + 1), 100 + attempts * 50);
      } else {
        console.warn(`[TicketRequest] Toast (${type}): ${message}`);
      }
    };
    tryShowToast();
  }

  resetCaptcha() {
    this.captchaToken = null;
    if (window.turnstile && this.captchaContainer) {
      try {
        window.turnstile.reset(this.captchaContainer);
      } catch (e) { /* ignore */ }
    }
    if (window.hcaptcha && this.captchaContainer) {
      try {
        window.hcaptcha.reset();
      } catch (e) { /* ignore */ }
    }
  }

  isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new TicketRequestPage());
} else {
  new TicketRequestPage();
}
